import { z } from 'zod';

/**
 * Plano financeiro — o plano de contas editavel.
 *
 * Ate a tela existir, grupos e categorias eram a constante `PLANO_DE_CONTAS`
 * em categories.ts, e mudar o plano exigia deploy. Ela continua la como carga
 * inicial da migration e como referencia do que a casa usava; a fonte da
 * verdade em tempo de execucao passou a ser o banco.
 *
 * `transactions.category` guarda o NOME da categoria, e nao um id. E por isso
 * que renomear precisa propagar para os lancamentos e que o nome e unico no
 * plano inteiro: dois grupos com a mesma categoria deixariam a DRE sem saber
 * onde somar.
 */

export interface CategoriaDoPlano {
  id: number;
  nome: string;
  /** Codigo da conta no plano de contas da contabilidade. Alimenta a exportacao. */
  codigo_contabil: string | null;
  ordem: number;
  /** Categoria desativada some do seletor mas continua valendo no historico. */
  is_active: boolean;
  /** Quantos lancamentos ja usam. Decide se da para excluir ou so desativar. */
  em_uso: number;
}

export interface GrupoDoPlano {
  id: number;
  nome: string;
  /** `receita` entra positivo no extrato; `despesa`, negativo. */
  natureza: 'receita' | 'despesa';
  ordem: number;
  categorias: CategoriaDoPlano[];
}

const nome = z
  .string()
  .trim()
  .min(2, 'O nome precisa de ao menos 2 caracteres.')
  .max(128, 'O nome passa de 128 caracteres.');

/**
 * Codigo contabil como corpo de requisicao.
 *
 * A distincao entre ausente e nulo aqui e obrigatoria, nao estilo: a tela do
 * plano reenvia o registro inteiro para desativar/reativar uma conta. Se
 * ausente significasse "limpe", desativar uma conta apagaria o codigo contabil
 * em silencio. Entao: ausente = nao mexe; `null` ou string vazia = limpa.
 */
export const codigoContabilBody = z
  .string()
  .trim()
  .max(32, 'O codigo contabil passa de 32 caracteres.')
  .transform((valor) => (valor === '' ? null : valor))
  .nullable()
  .optional();

export const categoriaDoPlanoBody = z.object({
  nome,
  group_id: z.number().int().positive(),
  is_active: z.boolean().optional(),
  codigo_contabil: codigoContabilBody,
});

export type CategoriaDoPlanoBody = z.infer<typeof categoriaDoPlanoBody>;

/** Lista plana de nomes ativos, na ordem dos grupos — alimenta o seletor. */
export function categoriasAtivas(plano: readonly GrupoDoPlano[]): string[] {
  return plano.flatMap((g) => g.categorias.filter((c) => c.is_active).map((c) => c.nome));
}

/** Grupo a que uma categoria pertence, ou `null` se ela saiu do plano. */
export function grupoDoNome(
  plano: readonly GrupoDoPlano[],
  categoria: string,
): GrupoDoPlano | null {
  return plano.find((g) => g.categorias.some((c) => c.nome === categoria)) ?? null;
}
